import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { LOGO_URL } from '@/lib/api';
import { ShieldCheck, Sparkles, BarChart3, Presentation } from 'lucide-react';

const PERKS = [
  { icon: BarChart3, title: 'Auto-synced KPIs', text: 'Production, maintenance and NPD data flow straight from JSON backups.' },
  { icon: ShieldCheck, title: 'Controlled reviews', text: 'Section-wise ownership, submissions and approvals per unit cycle.' },
  { icon: Presentation, title: 'One-click PPT', text: 'Generate the monthly review deck from live data in seconds.' },
];

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await login(email, password);
      nav('/');
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row app-canvas bg-[var(--aampl-bg)]">
      <div className="flex-1 flex items-center justify-center p-8 sm:p-12">
        <div className="max-w-md w-full">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-white border border-[var(--aampl-border)] shadow-sm flex items-center justify-center overflow-hidden">
              <img src={LOGO_URL} alt="AAMPL" className="w-11 h-11 object-contain" onError={(e)=>{e.currentTarget.style.display='none'}} />
            </div>
            <div>
              <div className="font-extrabold text-2xl tracking-tight text-[var(--aampl-text)] leading-none">AAMPL</div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #4F6BFF, #8B5CF6)' }}>MRM System</div>
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[var(--aampl-text)] leading-[1.05] mb-4">
            Management Review,<br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #4F6BFF, #8B5CF6)' }}>beautifully organised.</span>
          </h1>
          <p className="text-slate-500 mb-8 max-w-md">A unified portal to plan, track and present your monthly management review — from upload to PPT.</p>
          <div className="space-y-3">
            {PERKS.map((p) => (
              <div key={p.title} className="glass-card flex items-start gap-3 p-4">
                <span className="icon-chip w-10 h-10" style={{ background: '#EEF3FF', color: '#4F6BFF' }}><p.icon size={18} /></span>
                <div>
                  <div className="text-sm font-semibold text-[var(--aampl-text)]">{p.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.text}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="eyebrow mt-10 text-slate-400">IATF 16949 • ISO 14001 • ISO 50001</div>
        </div>
      </div>
      <div className="w-full lg:w-[480px] flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm glass-card p-7 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} className="text-[var(--aampl-primary)]" />
            <h2 className="text-2xl font-extrabold tracking-tight text-[var(--aampl-text)]">Welcome back</h2>
          </div>
          <div className="text-sm text-slate-500 mb-6">Sign in to continue to AAMPL MRM</div>
          <div className="space-y-4">
            <div>
              <label className="aampl-label">Email</label>
              <input className="aampl-input" type="email" placeholder="you@company.com" autoComplete="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="input-email" />
            </div>
            <div>
              <label className="aampl-label">Password</label>
              <input className="aampl-input" type="password" placeholder="Enter your password" autoComplete="current-password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="input-password" />
            </div>
            {err && <div className="text-[#DC2626] text-sm bg-[#FFF1F2] border border-[#FECDD3] rounded-xl px-3 py-2" data-testid="login-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full py-3" data-testid="btn-login">{busy ? 'Signing in…' : 'Sign In'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
